//@ts-nocheck
import React from 'react';

import { useFormikContext } from 'formik';
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  Chip,
  createFilterOptions,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import theme from 'theme';

export const fieldStyles = makeStyles(() => ({
  errorText: {
    color: '#E53935',
    fontSize: '11px',
    marginLeft: '10px',
  },
  labelRoot: {
    marginLeft: '0 !important',
    marginRight: 0,
    width: '100%',
  },
  labelPlacementTop: {
    alignItems: 'flex-start !important',
  },
  textInput: {
    marginTop: theme.spacing(0.5),
    width: '100%',
    border: 0,
    '& .MuiFilledInput-inputMarginDense': {
      paddingLeft: theme.spacing(1.5),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      width: '100%',
    },
  },
  autocomplete: {
    marginTop: theme.spacing(0.25),
    width: '100%',
    border: 0,
  },
}));

export interface DropdownOption {
  label: string;
  value: string;
}

/**
 * Function to extract the property from an object based on a property access string.
 */
export const getValueFromFieldArrayByString = (Object.byString = function (object, string) {
  string = string.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
  string = string.replace(/^\./, ''); // strip a leading dot
  var a = string.split('.');
  for (var i = 0, n = a.length; i < n; ++i) {
    var k = a[i];
    if (k in object) {
      object = object[k];
    } else {
      return;
    }
  }
  return object;
});

interface ValidatedDropdownProps {
  label: string;
  name: string;
  options: DropdownOption[];
  id?: string;
  isMultiChoice?: boolean;
  isDisabled?: boolean;
  loading?: boolean;
  action?: (result) => void;
  textFieldLabel?: string;
  backgroundColor?: string;
  propFormikContext?: any;
  restTextfieldProps?: any;
}

/**
 * A dropdown field component.
 * @param label The displayed label (helper text) of the field.
 * @param name The name to be used as the field name in the Formik form.
 * @param options The possible options for the dropdown.
 * @param isMultiChoice Set whether multiple choices are allowed.
 * @param isDisabled Set whether the field is disabled.
 * @param id A custom ID given for the dropdown.
 * @param loading Indicates whether the field values are being loaded.
 * @param action Action to be executed on value change, if not default.
 * @param textFieldLabel Label to be displayed on the text field.
 * @param propFormikContext Formik context to be used when the dropdown is in a different context.
 * @param searchInLabelAndValue search for the value both in value and label (if not set, search in value only)
 */

const ValidatedDropdown: React.FC<ValidatedDropdownProps & Partial<AutocompleteProps<any>>> = ({
  label,
  name,
  options,
  id,
  isMultiChoice = false,
  isDisabled = false,
  loading,
  action,
  textFieldLabel,
  backgroundColor,
  propFormikContext,
  restTextfieldProps,
  searchInLabelAndValue = false,
  placeholder,
  ...rest
}) => {
  const fieldClasses = fieldStyles();
  const fc = useFormikContext();
  const formikContext = propFormikContext ?? fc;
  const itemValue =
    name.includes('[') || name.includes('.')
      ? getValueFromFieldArrayByString(formikContext.values, name)
      : formikContext?.values[`${name}`];
  const filterOptions = createFilterOptions({
    stringify: (option) => (searchInLabelAndValue ? `${option.label}|${option.value}` : `${option.label}`),
  });

  return (
    <Autocomplete
      key={name}
      options={options ? options : []}
      filterOptions={filterOptions}
      data-testid={id ? `${id}-autocomplete` : `${name}-autocomplete`}
      id={`${name}-autocomplete`}
      disabled={isDisabled}
      disableCloseOnSelect={isMultiChoice}
      loading={loading}
      size="small"
      getOptionLabel={(option) => {
        return option.label;
      }}
      value={
        loading || !options
          ? null
          : isMultiChoice
            ? (options.filter((option) => itemValue && itemValue.includes(option.value)) ?? null)
            : (options.find((option) => option.value === itemValue) ?? null)
      }
      onChange={(_, value) => {
        if (action) {
          action(value);
        } else {
          formikContext.setFieldValue(name, isMultiChoice ? value.map((v) => v.value) : value ? value.value : value);
          formikContext.setFieldTouched(name, true, false);
          setTimeout(() => {
            formikContext.validateForm();
          });
        }
      }}
      onBlur={() => fc?.setFieldTouched(name, true)}
      multiple={isMultiChoice}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            variant="outlined"
            label={option.label}
            id={`${name}-chip-${index}`}
            size="small"
            style={{ padding: 0, margin: 0 }}
            {...getTagProps({ index })}
          />
        ))
      }
      renderInput={(params: AutocompleteRenderInputParams) => {
        return (
          <FormControlLabel
            label={label}
            id={`${name}-input-label`}
            labelPlacement="top"
            className={fieldClasses.autocomplete}
            classes={{
              root: fieldClasses.labelRoot,
              labelPlacementTop: fieldClasses.labelPlacementTop,
            }}
            control={
              <TextField
                {...params}
                label={textFieldLabel}
                className={fieldClasses.autocomplete}
                error={formikContext?.errors.hasOwnProperty(name)}
                helperText={formikContext?.touched.hasOwnProperty(name) && formikContext.errors[`${name}`]}
                fullWidth={true}
                style={{ backgroundColor: backgroundColor }}
                inputProps={{
                  ...params.inputProps,
                  'data-testid': `${name}-content-input`,
                  id: `${name}-content-input`,
                  style: { backgroundColor: backgroundColor },
                }}
                variant="filled"
                margin="dense"
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  style: { backgroundColor: backgroundColor },
                }}
                placeholder={placeholder}
                {...restTextfieldProps}
              />
            }
          />
        );
      }}
      {...rest}
    />
  );
};

export default ValidatedDropdown;
